(() => {
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function isDialogSupported() {
    return typeof HTMLDialogElement !== "undefined";
  }

  function lockBody(lock) {
    document.documentElement.classList.toggle("rl-modal-open", !!lock);
  }

  function lazyLoadIframes(root) {
    $$(root, "iframe[data-src]").forEach((ifr) => {
      if (!ifr.getAttribute("src")) {
        ifr.setAttribute("src", ifr.getAttribute("data-src") || "");
      }
    });
  }

  // --- Hours parsing: supports "Mo-Sa 09:00-18:00" and "Su 10:00-16:00" lines
  const DAY = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const DAY_IDX = { Su: 0, Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6 };

  function parseTimeToMinutes(t) {
    const m = /^(\d{1,2}):(\d{2})$/.exec((t || "").trim());
    if (!m) return null;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 60 + mm;
  }

  function expandDays(token) {
    // "Mo-Sa" or "Mo" etc
    const s = token.trim();
    if (!s) return [];
    if (s.includes("-")) {
      const [a, b] = s.split("-").map((x) => x.trim());
      if (!(a in DAY_IDX) || !(b in DAY_IDX)) return [];
      const out = [];
      let i = DAY_IDX[a];
      const end = DAY_IDX[b];
      // range can wrap (rare), handle anyway
      while (true) {
        out.push(i);
        if (i === end) break;
        i = (i + 1) % 7;
        if (out.length > 7) break;
      }
      return out;
    }
    if (s in DAY_IDX) return [DAY_IDX[s]];
    return [];
  }

  function parseOpeningHoursLines(raw) {
    const lines = (raw || "")
      .toString()
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    // result: map dayIndex -> [{start,end,label}]
    const map = new Map();

    lines.forEach((line) => {
      // e.g. "Mo-Sa 09:00-18:00"
      const parts = line.split(/\s+/);
      if (parts.length < 2) return;

      const dayToken = parts[0];
      const timeToken = parts.slice(1).join(" ").trim();
      const tm = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/.exec(timeToken);
      if (!tm) return;

      const start = parseTimeToMinutes(tm[1]);
      const end = parseTimeToMinutes(tm[2]);
      if (start == null || end == null) return;

      const days = expandDays(dayToken);
      days.forEach((d) => {
        const arr = map.get(d) || [];
        arr.push({ start, end, label: line });
        map.set(d, arr);
      });
    });

    return map;
  }

  function formatMinutes(mins) {
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(hh)}:${pad(mm)}`;
  }

  function todaysRangeLabel(ranges) {
    if (!ranges || !ranges.length) return "";
    const r = ranges[0];
    return `${formatMinutes(r.start)}–${formatMinutes(r.end)}`;
  }

  function isOpenNow(ranges, nowMins) {
    if (!ranges || !ranges.length) return false;
    return ranges.some((r) => nowMins >= r.start && nowMins < r.end);
  }

  function applyOpenUI(root, hoursRaw) {
    const badge = $(root, "[data-rl-open-badge]");
    const todayEl = $(root, "[data-rl-today]");
    const openStateEl = $(root, "[data-rl-openstate]");

    if (!badge && !todayEl && !openStateEl) return;

    const map = parseOpeningHoursLines(hoursRaw);
    const now = new Date();
    const dayIdx = now.getDay();
    const nowMins = now.getHours() * 60 + now.getMinutes();

    const ranges = map.get(dayIdx) || [];
    const todayLabel = todaysRangeLabel(ranges);
    const open = isOpenNow(ranges, nowMins);

    // Today box (keep it tidy: hide if empty, include open state here by default)
    if (todayEl) {
      if (todayLabel) {
        todayEl.textContent = `${DAY[dayIdx]}: ${todayLabel}  •  ${open ? "Open now" : "Closed"}`;
        todayEl.hidden = false;
      } else {
        todayEl.textContent = "";
        todayEl.hidden = true;
      }
    }

    // Optional separate open state hook (only if you have an element for it)
    if (openStateEl) {
      openStateEl.textContent = ranges.length ? (open ? "Open now" : "Closed") : "";
      openStateEl.hidden = !ranges.length;
    }

    if (badge) {
      badge.classList.remove("is-open", "is-closed");
      badge.classList.add(open ? "is-open" : "is-closed");
      badge.textContent = open ? "Open" : "Closed";
    }
  }

  function sanitizeInjected(root) {
    // Injected modal content must not contain triggers
    $$(root, "[data-rl-open]").forEach((el) => el.removeAttribute("data-rl-open"));
    // Strip nested templates if they sneak in
    $$(root, "template[data-rl-template]").forEach((t) => t.remove());
  }

  function getHoursSource(cardEl, injectedRoot) {
    // Prefer injected modal markup’s hours if present; fallback to card dataset.
    const fromModal = injectedRoot?.getAttribute?.("data-rl-hours");
    if (fromModal) return fromModal;
    return cardEl?.getAttribute?.("data-rl-hours") || "";
  }

  function openDialog(sectionEl, cardEl) {
    const dialog = $(sectionEl, "[data-rl-dialog]");
    const body = $(sectionEl, "[data-rl-body]");
    const closeBtn = $(sectionEl, "[data-rl-close]");
    const tpl = $(cardEl, "template[data-rl-template]");

    if (!dialog || !body || !tpl) return;

    body.innerHTML = tpl.innerHTML || "";
    sanitizeInjected(body);

    // Apply open/today UI (modal first, card as fallback)
    const modalRoot = $(body, "[data-rl-modal]") || body.firstElementChild;
    const hoursRaw = getHoursSource(cardEl, modalRoot);
    applyOpenUI(body, hoursRaw);

    // Lazy load map iframe(s) if present
    lazyLoadIframes(body);

    if (isDialogSupported() && typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }

    lockBody(true);
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function closeDialog(sectionEl) {
    const dialog = $(sectionEl, "[data-rl-dialog]");
    const body = $(sectionEl, "[data-rl-body]");
    if (!dialog) return;

    if (isDialogSupported() && typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }

    if (body) body.innerHTML = "";
    lockBody(false);
  }

  function wireSection(sectionEl) {
    const dialog = $(sectionEl, "[data-rl-dialog]");
    const closeBtn = $(sectionEl, "[data-rl-close]");

    // Set open badges on grid cards too
    $$(sectionEl, "[data-rl-open]").forEach((card) => {
      applyOpenUI(card, card.getAttribute("data-rl-hours") || "");
    });

    // Open modal when clicking a card (but not when clicking real interactive elements)
    sectionEl.addEventListener("click", (ev) => {
      const trigger = ev.target.closest?.("[data-rl-open]");
      if (!trigger || !sectionEl.contains(trigger)) return;

      // Let real links/buttons behave normally
      const interactive = ev.target.closest?.("a, button, input, select, textarea, label");
      if (interactive) return;

      ev.preventDefault();
      openDialog(sectionEl, trigger);
    });

    if (closeBtn) closeBtn.addEventListener("click", () => closeDialog(sectionEl));

    if (dialog) {
      // Click outside inner closes (backdrop click)
      dialog.addEventListener("click", (ev) => {
        const inner = $(dialog, ".retail-locations__dialog-inner");
        if (inner && inner.contains(ev.target)) return;
        closeDialog(sectionEl);
      });

      // ESC
      dialog.addEventListener("cancel", (ev) => {
        ev.preventDefault();
        closeDialog(sectionEl);
      });

      // If closed by anything else, cleanup
      dialog.addEventListener("close", () => {
        const body = $(sectionEl, "[data-rl-body]");
        if (body) body.innerHTML = "";
        lockBody(false);
      });
    }
  }

  function init() {
    $$(document, ".retail-locations").forEach(wireSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();